import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import { Stage, Layer, Line } from "react-konva";
import { CURRICULUM, GRADES, getAllChapters } from "../config/curriculum";
import { formatLatexForDisplay, repairLatexEscapes } from "../lib/latexUtils";
import ErrorBoundary from "../components/ErrorBoundary";

const ALL_CHAPTERS = getAllChapters();

export default function CreateQuestionPage({ apiBase, token, onPageBusyChange }) {
  const navigate = useNavigate();

  // Workflow state
  const [creationMode, setCreationMode] = useState("single"); // "single" | "multiple"
  const [creationStep, setCreationStep] = useState("input"); // "input" | "review"
  const [inputTab, setInputTab] = useState("upload"); // "upload" | "camera" | "stylus"
  const [isProcessingOcr, setIsProcessingOcr] = useState(false);
  const [ocrStatusMessage, setOcrStatusMessage] = useState("");
  const [ocrProgress, setOcrProgress] = useState({
    phase: "idle", // "idle" | "scanning" | "transcribing" | "done"
    totalQuestions: 0,
    completedQuestions: 0,
    currentBatch: 0,
    totalBatches: 0,
    batchStart: 0,
    batchEnd: 0
  });
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [ocrError, setOcrError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [exitTargetRoute, setExitTargetRoute] = useState("/my-questions");

  // Single Question Draft Form Data
  const [singleFormData, setSingleFormData] = useState({
    title: "",
    latex: "",
    grade: "Grade 10",
    chapters: ["Quadratic Equations"],
    difficulty: "MOT",
    imageUrl: ""
  });

  // Single Question Tutor Assignment Draft
  const [singleAssignment, setSingleAssignment] = useState({
    assignToClassroom: false,
    assignedAll: true,
    assignedTo: [],
    dueDate: "",
    instructions: ""
  });

  // Multiple Questions Draft Form Data
  const [setFormData, setSetFormData] = useState({
    title: "Homework Assignment",
    description: "",
    grade: "Grade 11",
    chapters: ["Circle"],
    sourceType: "homework",
    imageUrl: "",
    assignedTo: [],
    assignedAll: true,
    dueDate: "",
    instructions: "",
    questions: []
  });

  // Tutor Roster for assignments
  const [isTutor, setIsTutor] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [rosterStudents, setRosterStudents] = useState([]);

  // Uploaded media preview
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");

  // Stylus Canvas state
  const [canvasLines, setCanvasLines] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [penColor, setPenColor] = useState("#0f172a"); // Black or Blue
  const [canvasTool, setCanvasTool] = useState("pen"); // "pen" | "eraser"
  const canvasHeight = 480;
  const [canvasSize, setCanvasSize] = useState({ width: 720, height: 480 });
  const strokeWidth = canvasTool === "eraser" ? 24 : 3.0;

  // Refs
  const stageRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const [videoDevices, setVideoDevices] = useState([]);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);
  const [cameraActive, setCameraActive] = useState(false);
  const [isCameraLoading, setIsCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [currentFacingMode, setCurrentFacingMode] = useState("environment");
  const ocrAbortControllerRef = useRef(null);

  // Active expanded questions in review
  const [expandedQuestionIndexes, setExpandedQuestionIndexes] = useState(() => new Set([0]));
  const [openChapterDropdownIdx, setOpenChapterDropdownIdx] = useState(null);

  useEffect(() => {
    if (openChapterDropdownIdx === null) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest(".chapter-dropdown-wrapper")) {
        setOpenChapterDropdownIdx(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openChapterDropdownIdx]);

  useEffect(() => {
    if (!token) return;
    fetch(`${apiBase}/api/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((user) => {
        if (user?.role === "tutor") {
          setIsTutor(true);
          fetch(`${apiBase}/api/tutor/students`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => (res.ok ? res.json() : { students: [] }))
            .then((data) => {
              setRosterStudents(data.students || []);
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [apiBase, token]);

  const toggleQuestionExpanded = (index) => {
    setExpandedQuestionIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Check if user has unsaved work
  const hasUnsavedWork = useMemo(() => {
    if (creationStep === "review") return true;
    if (canvasLines.length > 0) return true;
    if (imagePreviewUrl) return true;
    return false;
  }, [creationStep, canvasLines.length, imagePreviewUrl]);

  // Detect mobile / tablet device
  const isMobileDevice = useMemo(() => {
    return typeof navigator !== "undefined" && (
      /Mobi|Android|iPhone|iPad|iPod|Silk|Kindle/i.test(navigator.userAgent) ||
      (typeof window !== "undefined" && "ontouchstart" in window && navigator.maxTouchPoints > 1)
    );
  }, []);

  // Check if WebRTC getUserMedia is available in current context (HTTPS or localhost)
  const hasGetUserMedia = useMemo(() => {
    return typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
  }, []);

  // Window beforeunload guard
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedWork && !isSaving) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedWork, isSaving]);

  // Request exit navigation
  const requestExit = (targetRoute = "/my-questions") => {
    if (hasUnsavedWork && !isSaving) {
      setExitTargetRoute(targetRoute);
      setShowExitConfirm(true);
    } else {
      navigate(targetRoute);
    }
  };

  const confirmExit = () => {
    setShowExitConfirm(false);
    stopCamera();
    navigate(exitTargetRoute);
  };

  // Resize observer for stylus canvas
  useEffect(() => {
    function updateCanvasSize() {
      if (!canvasContainerRef.current) return;
      const containerWidth = canvasContainerRef.current.clientWidth || 720;
      const width = Math.max(280, Math.floor(containerWidth - 6));
      setCanvasSize({ width, height: canvasHeight });
    }
    if (creationStep === "input" && inputTab === "stylus" && creationMode === "single") {
      updateCanvasSize();
      window.addEventListener("resize", updateCanvasSize);
    }
    return () => window.removeEventListener("resize", updateCanvasSize);
  }, [creationStep, inputTab, creationMode, canvasHeight]);

  // Stop camera stream cleanly
  const stopCamera = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {}
      });
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setIsCameraLoading(false);
    setCameraError("");
  }, []);

  // Start camera stream with environment-facing default & robust constraint cascade
  const startCamera = useCallback(async (facing = "environment", targetDeviceId = null) => {
    // Clean up any existing stream tracks first
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {}
      });
      mediaStreamRef.current = null;
    }

    setCameraError("");
    setOcrError("");

    if (typeof window === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setIsCameraLoading(false);
      setCameraActive(false);
      return;
    }

    setIsCameraLoading(true);
    setCameraActive(false);

    try {
      const videoConstraints = {
        width: { ideal: 1920, min: 640 },
        height: { ideal: 1080, min: 480 }
      };

      if (targetDeviceId) {
        videoConstraints.deviceId = { exact: targetDeviceId };
      } else {
        videoConstraints.facingMode = { ideal: facing };
      }

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false
        });
      } catch (constraintErr) {
        console.warn("[CreateQuestionPage] High-res constraints failed, falling back to basic:", constraintErr);
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: facing } },
            audio: false
          });
        } catch (_) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false
          });
        }
      }

      // Enumerate available video inputs now that permissions have been granted
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const inputs = devices.filter((d) => d.kind === "videoinput");
        if (inputs.length > 0) {
          setVideoDevices(inputs);

          // On Windows, Chromium ignores facingMode: "environment" and opens device 0 (front camera).
          // If we requested the rear camera and did not specify targetDeviceId, check if there is a rear-facing camera:
          if (facing === "environment" && !targetDeviceId && inputs.length > 1) {
            const rearDevice = inputs.find((d) => /rear|back|environment|world/i.test(d.label));
            const currentTrack = stream.getVideoTracks()[0];
            const currentLabel = currentTrack?.label || "";
            if (rearDevice && rearDevice.deviceId && /front|user|face/i.test(currentLabel)) {
              stream.getTracks().forEach((t) => t.stop());
              stream = await navigator.mediaDevices.getUserMedia({
                video: {
                  deviceId: { exact: rearDevice.deviceId },
                  width: { ideal: 1920, min: 640 },
                  height: { ideal: 1080, min: 480 }
                },
                audio: false
              });
            }
          }
        }
      } catch (_) {}

      mediaStreamRef.current = stream;

      // Attach stream to video DOM element
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.setAttribute("webkit-playsinline", "true");
        video.muted = true;
        try {
          await video.play();
        } catch (playErr) {
          console.warn("[CreateQuestionPage] video.play() error:", playErr);
        }
      }

      setCameraActive(true);
      setIsCameraLoading(false);
      setCurrentFacingMode(facing);
    } catch (err) {
      console.error("[CreateQuestionPage] Camera stream error:", err);
      setIsCameraLoading(false);
      setCameraActive(false);

      let msg = "Could not access camera. Please check your browser camera permissions.";
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        msg = "Camera permission was denied. Please allow camera access in your browser settings.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        msg = "No camera was detected on this device.";
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        msg = "Camera is currently being used by another application. Please close it and retry.";
      }
      setCameraError(msg);
    }
  }, []);

  // Flip / switch camera between front and back
  const flipCamera = useCallback(async () => {
    if (videoDevices.length > 1) {
      const nextIdx = (currentDeviceIndex + 1) % videoDevices.length;
      setCurrentDeviceIndex(nextIdx);
      const targetDevice = videoDevices[nextIdx];
      const nextFacing = /front|user|face/i.test(targetDevice?.label) ? "user" : "environment";
      setCurrentFacingMode(nextFacing);
      await startCamera(nextFacing, targetDevice?.deviceId);
    } else {
      const nextFacing = currentFacingMode === "environment" ? "user" : "environment";
      setCurrentFacingMode(nextFacing);
      await startCamera(nextFacing);
    }
  }, [videoDevices, currentDeviceIndex, currentFacingMode, startCamera]);

  // Keep video.srcObject synchronized whenever cameraActive changes
  useEffect(() => {
    if (cameraActive && mediaStreamRef.current && videoRef.current) {
      const video = videoRef.current;
      if (video.srcObject !== mediaStreamRef.current) {
        video.srcObject = mediaStreamRef.current;
        video.setAttribute("playsinline", "true");
        video.setAttribute("webkit-playsinline", "true");
        video.muted = true;
        video.play().catch(() => {});
      }
    }
  }, [cameraActive]);

  // Automatically start camera when user enters camera tab, stop on exit
  useEffect(() => {
    if (creationStep === "input" && inputTab === "camera" && !isProcessingOcr) {
      if (hasGetUserMedia) {
        startCamera(currentFacingMode);
      } else {
        stopCamera();
      }
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [creationStep, inputTab, isProcessingOcr, currentFacingMode, hasGetUserMedia, startCamera, stopCamera]);

  // Cancel in-flight OCR
  const cancelOcrTranscription = useCallback(() => {
    if (ocrAbortControllerRef.current) {
      ocrAbortControllerRef.current.abort();
      ocrAbortControllerRef.current = null;
    }
    setIsProcessingOcr(false);
    setOcrStatusMessage("");
    setOcrProgress({
      phase: "idle",
      totalQuestions: 0,
      completedQuestions: 0,
      currentBatch: 0,
      totalBatches: 0,
      batchStart: 0,
      batchEnd: 0
    });
    setShowCancelConfirm(false);
  }, []);

  // Run AI Transcription: Single Question
  const runSingleOcr = useCallback(async (dataUrl, options = {}) => {
    if (!token || !dataUrl) return;
    cancelOcrTranscription();
    const controller = new AbortController();
    ocrAbortControllerRef.current = controller;

    setIsProcessingOcr(true);
    setOcrProgress({
      phase: "transcribing",
      totalQuestions: 1,
      completedQuestions: 0,
      currentBatch: 1,
      totalBatches: 1,
      batchStart: 1,
      batchEnd: 1
    });
    setOcrStatusMessage("Reading math problem and equations...");
    setOcrError("");

    try {
      const res = await fetch(`${apiBase}/api/questions/parse-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          dataUrl,
          isStylus: Boolean(options.isStylus)
        }),
        signal: controller.signal
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Could not read question. Please try a clearer picture.");
      }

      const result = await res.json();
      const detectedGrade = GRADES.includes(result.grade) ? result.grade : "Grade 10";
      const detectedChapters = Array.isArray(result.chapters) && result.chapters.length
        ? result.chapters
        : (result.chapter ? [result.chapter] : ["Others"]);

      setSingleFormData({
        title: result.title || "Custom Problem",
        latex: repairLatexEscapes(result.latex || ""),
        grade: detectedGrade,
        chapters: detectedChapters,
        difficulty: result.difficulty || "MOT",
        imageUrl: options.isStylus ? "" : (result.imageUrl || dataUrl)
      });
      setOcrProgress({
        phase: "done",
        totalQuestions: 1,
        completedQuestions: 1,
        currentBatch: 1,
        totalBatches: 1,
        batchStart: 1,
        batchEnd: 1
      });
      setCreationStep("review");
    } catch (err) {
      if (err.name === "AbortError") {
        setOcrError("Reading cancelled.");
      } else {
        setOcrError(err.message || "Could not read question. Please try a clearer picture.");
      }
    } finally {
      if (ocrAbortControllerRef.current === controller) {
        ocrAbortControllerRef.current = null;
      }
      setIsProcessingOcr(false);
      setOcrStatusMessage("");
    }
  }, [apiBase, token, cancelOcrTranscription]);

  // Run AI Transcription: Multiple Questions (Document / Worksheet) - Two-Phase Progressive Batching
  const runDocumentOcr = useCallback(async (dataUrl, defaultTitle = "") => {
    if (!token || !dataUrl) return;
    cancelOcrTranscription();
    const controller = new AbortController();
    ocrAbortControllerRef.current = controller;

    setIsProcessingOcr(true);
    setOcrError("");
    setOcrProgress({
      phase: "scanning",
      totalQuestions: 0,
      completedQuestions: 0,
      currentBatch: 0,
      totalBatches: 0,
      batchStart: 0,
      batchEnd: 0
    });
    setOcrStatusMessage("Scanning worksheet and counting math questions...");

    try {
      // Phase 1: Rapid Question Indexing & Manifest Scan
      const scanRes = await fetch(`${apiBase}/api/questions/scan-document`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          dataUrl,
          defaultTitle: defaultTitle || setFormData.title
        }),
        signal: controller.signal
      });

      if (!scanRes.ok) {
        const body = await scanRes.json().catch(() => ({}));
        throw new Error(body.error || "Could not scan worksheet questions.");
      }

      const scanResult = await scanRes.json();
      const detectedCount = Number(scanResult.totalQuestions) || (Array.isArray(scanResult.questionManifest) ? scanResult.questionManifest.length : 0);
      const docSessionId = scanResult.docSessionId;
      const resolvedImageUrl = scanResult.storedImageUrl || dataUrl;
      const detectedSetTitle = scanResult.setTitle || defaultTitle || setFormData.title || "Homework Assignment";

      if (!detectedCount || detectedCount <= 0) {
        throw new Error("No clear math questions were detected in this document. Please check that your photo or PDF is legible.");
      }

      // Phase 2: Progressive Batch Transcription (Chunks of 6 questions for fast, reliable non-truncation)
      const BATCH_SIZE = 6;
      const totalBatches = Math.ceil(detectedCount / BATCH_SIZE);
      let allExtractedQuestions = [];

      setOcrProgress({
        phase: "transcribing",
        totalQuestions: detectedCount,
        completedQuestions: 0,
        currentBatch: 1,
        totalBatches,
        batchStart: 1,
        batchEnd: Math.min(BATCH_SIZE, detectedCount)
      });
      setOcrStatusMessage(`Found ${detectedCount} questions. Beginning transcription...`);

      for (let b = 0; b < totalBatches; b++) {
        if (controller.signal.aborted) break;

        const batchStart = b * BATCH_SIZE + 1;
        const batchEnd = Math.min((b + 1) * BATCH_SIZE, detectedCount);

        setOcrProgress({
          phase: "transcribing",
          totalQuestions: detectedCount,
          completedQuestions: allExtractedQuestions.length,
          currentBatch: b + 1,
          totalBatches,
          batchStart,
          batchEnd
        });
        setOcrStatusMessage(
          `Transcribing questions ${batchStart}–${batchEnd} of ${detectedCount} (${Math.round((allExtractedQuestions.length / detectedCount) * 100)}%)...`
        );

        let batchQuestions = [];
        let batchSuccess = false;
        let lastBatchErr = null;

        for (let attempt = 1; attempt <= 2; attempt++) {
          if (controller.signal.aborted) break;
          try {
            if (attempt > 1) {
              setOcrStatusMessage(
                `Retrying questions ${batchStart}–${batchEnd} (attempt ${attempt} of 2)...`
              );
              await new Promise((resolve) => setTimeout(resolve, 1500));
            }

            const batchRes = await fetch(`${apiBase}/api/questions/transcribe-batch`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                docSessionId,
                storedImageUrl: scanResult.storedImageUrl,
                dataUrl: docSessionId ? undefined : dataUrl,
                startQuestionNumber: batchStart,
                endQuestionNumber: batchEnd,
                questionManifest: scanResult.questionManifest
              }),
              signal: controller.signal
            });

            if (!batchRes.ok) {
              const body = await batchRes.json().catch(() => ({}));
              throw new Error(body.error || `HTTP ${batchRes.status}`);
            }

            const batchData = await batchRes.json();
            batchQuestions = Array.isArray(batchData.questions) ? batchData.questions : [];
            batchSuccess = true;
            break;
          } catch (err) {
            lastBatchErr = err;
            if (controller.signal.aborted) break;
            console.warn(`[transcribe-batch] Batch ${batchStart}..${batchEnd} attempt ${attempt} failed:`, err.message);
          }
        }

        if (batchQuestions.length > 0) {
          allExtractedQuestions.push(...batchQuestions);
        } else if (!batchSuccess && allExtractedQuestions.length === 0 && b === 0) {
          throw new Error(lastBatchErr?.message || `Failed during transcription of questions ${batchStart} to ${batchEnd}.`);
        }

        setOcrProgress((prev) => ({
          ...prev,
          completedQuestions: allExtractedQuestions.length
        }));
      }

      if (controller.signal.aborted) {
        return;
      }

      if (allExtractedQuestions.length === 0) {
        throw new Error("Could not extract math questions from this document. Please try a clearer copy.");
      }

      // Re-index questions cleanly 1..N and repair any LaTeX escapes
      const finalQuestions = allExtractedQuestions.map((q, idx) => ({
        ...q,
        latex: repairLatexEscapes(q.latex || ""),
        questionNumber: idx + 1,
        imageUrl: resolvedImageUrl
      }));

      const defaultGrade = finalQuestions[0]?.grade || "Grade 11";
      const defaultChapters = finalQuestions[0]?.chapters || ["General"];

      setSetFormData((prev) => ({
        ...prev,
        title: detectedSetTitle,
        description: "",
        grade: defaultGrade,
        chapters: defaultChapters,
        sourceType: "homework",
        imageUrl: resolvedImageUrl,
        questions: finalQuestions,
        assignedAll: typeof prev?.assignedAll === "boolean" ? prev.assignedAll : true,
        assignedTo: Array.isArray(prev?.assignedTo) ? prev.assignedTo : [],
        dueDate: prev?.dueDate || "",
        instructions: prev?.instructions || ""
      }));

      setExpandedQuestionIndexes(new Set(finalQuestions.map((_, i) => i)));
      setOcrProgress({
        phase: "done",
        totalQuestions: finalQuestions.length,
        completedQuestions: finalQuestions.length,
        currentBatch: totalBatches,
        totalBatches,
        batchStart: 1,
        batchEnd: finalQuestions.length
      });
      setCreationStep("review");
    } catch (err) {
      if (err.name === "AbortError") {
        setOcrError("Worksheet transcription cancelled.");
      } else {
        setOcrError(err.message || "Could not read worksheet.");
      }
    } finally {
      if (ocrAbortControllerRef.current === controller) {
        ocrAbortControllerRef.current = null;
      }
      setIsProcessingOcr(false);
      setOcrStatusMessage("");
    }
  }, [apiBase, token, cancelOcrTranscription, setFormData.title]);

  // Process File Selection
  const handleFileSelect = useCallback((file) => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (!file || isProcessingOcr) return;

    if (file.size > 25 * 1024 * 1024) {
      setOcrError("The selected document exceeds 25 MB. Please upload a compressed PDF or fewer pages at a time.");
      return;
    }

    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf" || file.name.endsWith(".pdf");

    if (!isImage && !isPdf) {
      setOcrError("Please select an image file (PNG, JPG, WEBP) or a PDF document.");
      return;
    }

    stopCamera();
    setSelectedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImagePreviewUrl(dataUrl);
      setOcrError("");

      if (creationMode === "multiple") {
        const derivedTitle = file.name.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ");
        runDocumentOcr(dataUrl, derivedTitle);
      } else {
        runSingleOcr(dataUrl);
      }
    };
    reader.readAsDataURL(file);
  }, [isProcessingOcr, stopCamera, creationMode, runDocumentOcr, runSingleOcr]);

  // Clipboard paste listener
  useEffect(() => {
    if (creationStep !== "input" || isProcessingOcr) return;

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          const file = items[i].getAsFile();
          if (file) {
            handleFileSelect(file);
            e.preventDefault();
            break;
          }
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [creationStep, isProcessingOcr, handleFileSelect]);

  // Camera Capture Photo
  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      setOcrError("Camera is not active. Please start camera first.");
      return;
    }

    const width = video.videoWidth || video.clientWidth;
    const height = video.videoHeight || video.clientHeight;

    if (!width || !height) {
      setOcrError("Camera video stream is not ready. Please wait a moment and try again.");
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || width;
      canvas.height = video.videoHeight || height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.92);

      stopCamera();
      setImagePreviewUrl(dataUrl);
      setSelectedFileName("Camera Photo");

      if (creationMode === "multiple") {
        runDocumentOcr(dataUrl, "Homework Assignment");
      } else {
        runSingleOcr(dataUrl);
      }
    } catch (err) {
      setOcrError("Failed to capture photo frame: " + (err.message || String(err)));
    }
  }, [stopCamera, creationMode, runDocumentOcr, runSingleOcr]);

  // Stylus Drawing Handlers
  const handlePointerDown = (e) => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (!pos) return;
    setIsDrawing(true);
    setCanvasLines((prev) => [
      ...prev,
      {
        points: [pos.x, pos.y],
        color: canvasTool === "eraser" ? "#ffffff" : penColor,
        strokeWidth,
        isEraser: canvasTool === "eraser"
      }
    ]);
  };

  const handlePointerMove = (e) => {
    if (!isDrawing) return;
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (!pos) return;

    setCanvasLines((prev) => {
      const lastLine = prev[prev.length - 1];
      if (!lastLine) return prev;
      const newPoints = lastLine.points.concat([pos.x, pos.y]);
      const updatedLine = { ...lastLine, points: newPoints };
      return [...prev.slice(0, -1), updatedLine];
    });
  };

  const handlePointerUp = () => {
    setIsDrawing(false);
  };

  // Convert Stylus Strokes to LaTeX
  const convertStylusCanvas = () => {
    if (!stageRef.current || !canvasLines.length) {
      setOcrError("Please write something on the canvas before converting.");
      return;
    }
    try {
      const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
      setOcrError("");
      runSingleOcr(dataUrl, { isStylus: true });
    } catch (_) {
      setOcrError("Failed to capture handwritten canvas. Please try again.");
    }
  };

  // Single Question: Toggle Chapter Selection
  const toggleSingleChapter = (chapterName) => {
    setSingleFormData((prev) => {
      const exists = prev.chapters.includes(chapterName);
      let updated;
      if (exists) {
        updated = prev.chapters.filter((c) => c !== chapterName);
        if (!updated.length) updated = ["Others"];
      } else {
        updated = [...prev.chapters.filter((c) => c !== "Others"), chapterName];
      }
      return { ...prev, chapters: updated };
    });
  };

  // Multiple Questions: Toggle Chapter Selection for a specific question
  const toggleQuestionChapter = (questionIndex, chapterName) => {
    setSetFormData((prev) => {
      const questions = [...prev.questions];
      const targetQ = questions[questionIndex];
      if (!targetQ) return prev;

      const currentChapters = Array.isArray(targetQ.chapters) && targetQ.chapters.length
        ? targetQ.chapters
        : (targetQ.chapter ? [targetQ.chapter] : ["Others"]);

      const exists = currentChapters.includes(chapterName);
      let updatedChapters;
      if (exists) {
        updatedChapters = currentChapters.filter((c) => c !== chapterName);
        if (!updatedChapters.length) updatedChapters = ["Others"];
      } else {
        updatedChapters = [...currentChapters.filter((c) => c !== "Others"), chapterName];
      }

      questions[questionIndex] = {
        ...targetQ,
        chapters: updatedChapters,
        chapter: updatedChapters[0] || "Others",
        tags: updatedChapters
      };
      return { ...prev, questions };
    });
  };

  // Multiple Questions: Handle Master Grade Change across Assignment
  const handleSetGradeChange = (newGrade) => {
    setSetFormData((prev) => ({
      ...prev,
      grade: newGrade,
      questions: prev.questions.map((q) => ({
        ...q,
        grade: newGrade
      }))
    }));
  };

  // Multiple Questions: Update a specific question field
  const updateQuestionItem = (index, field, value) => {
    setSetFormData((prev) => {
      const questions = [...prev.questions];
      if (!questions[index]) return prev;
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, questions };
    });
  };

  // Multiple Questions: Delete a question
  const deleteQuestionItem = (index) => {
    setSetFormData((prev) => {
      const questions = prev.questions.filter((_, i) => i !== index);
      return { ...prev, questions };
    });
    setExpandedQuestionIndexes((prev) => {
      const next = new Set();
      Array.from(prev).forEach((idx) => {
        if (idx < index) next.add(idx);
        else if (idx > index) next.add(idx - 1);
      });
      return next;
    });
  };

  // Multiple Questions: Move Question Up
  const moveQuestionUp = (index) => {
    if (index <= 0) return;
    setSetFormData((prev) => {
      const questions = [...prev.questions];
      const temp = questions[index - 1];
      questions[index - 1] = questions[index];
      questions[index] = temp;
      return { ...prev, questions };
    });
  };

  // Multiple Questions: Move Question Down
  const moveQuestionDown = (index) => {
    if (index >= setFormData.questions.length - 1) return;
    setSetFormData((prev) => {
      const questions = [...prev.questions];
      const temp = questions[index + 1];
      questions[index + 1] = questions[index];
      questions[index] = temp;
      return { ...prev, questions };
    });
  };

  // Multiple Questions: Add Blank Question
  const addBlankQuestion = () => {
    const newIdx = setFormData.questions.length;
    const newQuestion = {
      questionNumber: newIdx + 1,
      title: `Problem ${newIdx + 1}`,
      latex: "x + y = 10",
      grade: setFormData.grade || "Grade 11",
      chapters: setFormData.chapters || ["General"],
      chapter: setFormData.chapters?.[0] || "General",
      tags: setFormData.chapters || ["General"],
      difficulty: "MOT",
      imageUrl: setFormData.imageUrl || ""
    };
    setSetFormData((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
    setExpandedQuestionIndexes((prev) => new Set([...prev, newIdx]));
  };

  // SAVE ACTIONS: Single Question
  const handleSaveSingleQuestion = async (shouldSolveNow = false) => {
    if (!singleFormData.latex || !String(singleFormData.latex).trim()) {
      setOcrError("Please provide the math equation for this problem.");
      return;
    }
    setIsSaving(true);
    setOcrError("");

    try {
      const res = await fetch(`${apiBase}/api/questions/custom`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          latex: repairLatexEscapes(singleFormData.latex || ""),
          title: singleFormData.title || "Custom Problem",
          grade: singleFormData.grade,
          chapters: singleFormData.chapters,
          chapter: singleFormData.chapters[0] || "Others",
          difficulty: singleFormData.difficulty,
          imageUrl: singleFormData.imageUrl
        })
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to save question.");
      }

      const data = await res.json();
      const createdId = data.question?._id || data.question?.id;

      // If tutor enabled assignment dispatch, assign question directly
      if (isTutor && singleAssignment.assignToClassroom && createdId) {
        try {
          await fetch(`${apiBase}/api/tutor/assign`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              questionId: createdId,
              title: singleFormData.title || "Custom Problem",
              assignedAll: singleAssignment.assignedAll,
              assignedTo: singleAssignment.assignedAll ? [] : singleAssignment.assignedTo,
              dueDate: singleAssignment.dueDate || null,
              instructions: singleAssignment.instructions || ""
            })
          });
        } catch (assignErr) {
          console.warn("Failed to dispatch assignment:", assignErr);
        }

        if (shouldSolveNow) {
          navigate(`/practice/custom/${createdId}`);
        } else {
          navigate("/tutor/assignments");
        }
        return;
      }

      if (shouldSolveNow && createdId) {
        navigate(`/practice/custom/${createdId}`);
      } else {
        navigate("/my-questions");
      }
    } catch (err) {
      setOcrError(err.message || "Failed to save question.");
      setIsSaving(false);
    }
  };

  // SAVE ACTIONS: Multiple Questions (Set)
  const handleSaveQuestionSet = async (shouldSolveNow = false) => {
    if (!setFormData.questions || setFormData.questions.length === 0) {
      setOcrError("Please include at least one question before saving.");
      return;
    }
    setIsSaving(true);
    setOcrError("");

    try {
      const res = await fetch(`${apiBase}/api/questions/custom-set`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: setFormData.title || "Homework Assignment",
          description: setFormData.description || "",
          sourceType: setFormData.sourceType || "homework",
          grade: setFormData.grade,
          chapters: setFormData.chapters,
          assignedTo: setFormData.assignedAll ? [] : setFormData.assignedTo,
          assignedAll: setFormData.assignedAll,
          dueDate: setFormData.dueDate || null,
          instructions: setFormData.instructions || "",
          questions: setFormData.questions.map((q, idx) => ({
            title: q.title || `Problem ${idx + 1}`,
            latex: repairLatexEscapes(q.latex || ""),
            grade: q.grade || setFormData.grade,
            chapters: q.chapters || setFormData.chapters,
            difficulty: q.difficulty || "MOT",
            imageUrl: q.imageUrl || setFormData.imageUrl || ""
          }))
        })
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to save question set.");
      }

      const data = await res.json();
      const firstQ = data.questions?.[0];

      if (shouldSolveNow && firstQ?._id) {
        navigate(`/practice/custom/${firstQ._id}`);
      } else if (isTutor) {
        navigate("/tutor/assignments");
      } else {
        navigate("/my-questions");
      }
    } catch (err) {
      setOcrError(err.message || "Failed to save question set.");
      setIsSaving(false);
    }
  };

  return (
    <div className="create-question-page" style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px 24px" }}>
      {/* ── Top Navigation & Header ── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "20px",
        flexWrap: "wrap",
        gap: "12px",
        borderBottom: "1px solid var(--border)",
        paddingBottom: "16px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            type="button"
            className="secondary-button"
            disabled={isProcessingOcr}
            onClick={() => requestExit("/my-questions")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: isProcessingOcr ? "not-allowed" : "pointer",
              opacity: isProcessingOcr ? 0.4 : 1,
              pointerEvents: isProcessingOcr ? "none" : "auto"
            }}
          >
            <span>←</span> Back to My Questions
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: "22px", fontWeight: "700", color: "#0f172a" }}>
              {creationStep === "input"
                ? (creationMode === "multiple" ? "Upload Homework / Assignment" : "Add Practice Question")
                : (creationMode === "multiple" ? "Review & Customize Assignment" : "Review Question & Topics")}
            </h1>
            <div className="question-meta" style={{ fontSize: "13px", color: "var(--muted)", marginTop: "2px" }}>
              {creationStep === "input"
                ? "Turn photos, worksheets, or your handwriting into interactive math problems"
                : "Check your math questions and choose chapters before solving"}
            </div>
          </div>
        </div>

        {/* Step Indicator */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{
            padding: "5px 12px",
            borderRadius: "16px",
            fontSize: "12px",
            fontWeight: "700",
            backgroundColor: creationStep === "input" ? "#2563eb" : "#f1f5f9",
            color: creationStep === "input" ? "#ffffff" : "#475569",
            boxShadow: creationStep === "input" ? "0 2px 6px rgba(37, 99, 235, 0.3)" : "none"
          }}>
            1. Input & Capture
          </span>
          <span style={{ color: "var(--muted)", fontSize: "12px" }}>→</span>
          <span style={{
            padding: "5px 12px",
            borderRadius: "16px",
            fontSize: "12px",
            fontWeight: "700",
            backgroundColor: creationStep === "review" ? "#2563eb" : "#f1f5f9",
            color: creationStep === "review" ? "#ffffff" : "#475569",
            boxShadow: creationStep === "review" ? "0 2px 6px rgba(37, 99, 235, 0.3)" : "none"
          }}>
            2. Review & Save
          </span>
        </div>
      </div>

      {ocrError && (
        <div className="error-text" style={{
          marginBottom: "16px",
          padding: "12px 16px",
          backgroundColor: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: "8px",
          fontSize: "13px"
        }}>
          ⚠️ {ocrError}
        </div>
      )}

      {/* ── STAGE 1: INPUT & CAPTURE ── */}
      {creationStep === "input" && (
        <div>
          {/* Mode Selector Cards (Locked during active transcription) */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "14px",
            marginBottom: "20px"
          }}>
            <div
              onClick={isProcessingOcr ? undefined : () => {
                setCreationMode("single");
                setOcrError("");
              }}
              style={{
                padding: "16px 18px",
                borderRadius: "12px",
                border: `2px solid ${creationMode === "single" ? "#2563eb" : "var(--border)"}`,
                backgroundColor: creationMode === "single" ? "#eff6ff" : "#ffffff",
                boxShadow: creationMode === "single" ? "0 4px 12px rgba(37, 99, 235, 0.12)" : "none",
                cursor: isProcessingOcr ? "not-allowed" : "pointer",
                opacity: isProcessingOcr ? 0.4 : 1,
                pointerEvents: isProcessingOcr ? "none" : "auto",
                transition: "all 0.15s ease"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                <span style={{ fontSize: "20px" }}>🎯</span>
                <span style={{ fontWeight: "700", fontSize: "15px", color: creationMode === "single" ? "#1d4ed8" : "#0f172a" }}>
                  Single Problem
                </span>
                {creationMode === "single" && (
                  <span style={{ marginLeft: "auto", fontSize: "11px", fontWeight: "700", color: "#2563eb", background: "#dbeafe", padding: "2px 8px", borderRadius: "999px" }}>
                    SELECTED
                  </span>
                )}
              </div>
              <div style={{ fontSize: "12px", color: "var(--muted)", lineHeight: "1.4" }}>
                Take a photo, upload an image, or write a problem directly with your pen.
              </div>
            </div>

            <div
              onClick={isProcessingOcr ? undefined : () => {
                setCreationMode("multiple");
                if (inputTab === "stylus") setInputTab("upload");
                setOcrError("");
              }}
              style={{
                padding: "16px 18px",
                borderRadius: "12px",
                border: `2px solid ${creationMode === "multiple" ? "#2563eb" : "var(--border)"}`,
                backgroundColor: creationMode === "multiple" ? "#eff6ff" : "#ffffff",
                boxShadow: creationMode === "multiple" ? "0 4px 12px rgba(37, 99, 235, 0.12)" : "none",
                cursor: isProcessingOcr ? "not-allowed" : "pointer",
                opacity: isProcessingOcr ? 0.4 : 1,
                pointerEvents: isProcessingOcr ? "none" : "auto",
                transition: "all 0.15s ease"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                <span style={{ fontSize: "20px" }}>📚</span>
                <span style={{ fontWeight: "700", fontSize: "15px", color: creationMode === "multiple" ? "#1d4ed8" : "#0f172a" }}>
                  Question Set (Homework / Assignment)
                </span>
                {creationMode === "multiple" && (
                  <span style={{ marginLeft: "auto", fontSize: "11px", fontWeight: "700", color: "#2563eb", background: "#dbeafe", padding: "2px 8px", borderRadius: "999px" }}>
                    SELECTED
                  </span>
                )}
              </div>
              <div style={{ fontSize: "12px", color: "var(--muted)", lineHeight: "1.4" }}>
                Upload a worksheet or exam sheet. We will automatically separate each question for you.
              </div>
            </div>
          </div>

          {/* Animated Scanning & Progress Experience */}
          {isProcessingOcr ? (
            <div style={{
              textAlign: "center",
              padding: "36px 24px",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
              borderRadius: "16px",
              border: "2px solid #93c5fd",
              boxShadow: "0 8px 30px rgba(37, 99, 235, 0.12)",
              marginBottom: "20px"
            }}>
              {/* Image Preview with Holographic Laser Scanning Beam */}
              {imagePreviewUrl ? (
                <div style={{ display: "inline-block", position: "relative", marginBottom: "20px", maxWidth: "420px", width: "100%" }}>
                  <div className="scan-container" style={{ border: "2px solid #38bdf8", maxHeight: "280px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8fafc" }}>
                    <div className="scan-laser-line" />
                    <div className="scan-overlay-shimmer" />
                    {imagePreviewUrl.startsWith("data:application/pdf") ? (
                      <div style={{ padding: "36px 24px", textAlign: "center" }}>
                        <div style={{ fontSize: "44px", marginBottom: "8px" }}>📄</div>
                        <div style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
                          {selectedFileName || "PDF Worksheet"}
                        </div>
                        <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "4px" }}>
                          Multi-page Mathematics Document
                        </div>
                      </div>
                    ) : (
                      <img
                        src={imagePreviewUrl}
                        alt="Scanning Preview"
                        style={{ width: "100%", maxHeight: "280px", objectFit: "contain", display: "block", opacity: 0.9 }}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "80px", height: "80px", borderRadius: "50%", background: "linear-gradient(135deg, #eff6ff, #dbeafe)", marginBottom: "16px", border: "2px solid #93c5fd" }}>
                  <div className="spinner" style={{ width: "36px", height: "36px", borderWidth: "3px" }} />
                </div>
              )}

              {/* Multi-stage Progress Indicators */}
              <div style={{ display: "flex", justifyContent: "center", gap: "10px", alignItems: "center", marginBottom: "16px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "12px", fontWeight: "700", color: "#10b981", background: "#ecfdf5", padding: "3px 10px", borderRadius: "999px", border: "1px solid #a7f3d0" }}>
                  ✓ Uploaded
                </span>
                <span style={{ fontSize: "12px", color: "var(--muted)" }}>→</span>
                <span style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#2563eb",
                  background: "#eff6ff",
                  padding: "3px 12px",
                  borderRadius: "999px",
                  border: "1px solid #bfdbfe",
                  animation: "pulseGlow 2s infinite ease-in-out"
                }}>
                  {ocrProgress.phase === "scanning"
                    ? "🔍 Scanning & Counting Questions..."
                    : ocrProgress.totalQuestions > 0
                      ? `🔍 Transcribing Math (${ocrProgress.completedQuestions} of ${ocrProgress.totalQuestions})`
                      : "🔍 Reading Math & Diagrams"}
                </span>
                <span style={{ fontSize: "12px", color: "var(--muted)" }}>→</span>
                <span style={{ fontSize: "12px", fontWeight: "600", color: "#94a3b8", background: "#f8fafc", padding: "3px 10px", borderRadius: "999px", border: "1px solid #e2e8f0" }}>
                  ✨ Ready to Practice
                </span>
              </div>

              {/* Quantitative Live Progress Bar */}
              {ocrProgress.totalQuestions > 0 ? (
                <div style={{ maxWidth: "480px", margin: "0 auto 20px auto", width: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", fontSize: "13px", fontWeight: "700" }}>
                    <span style={{ color: "#1e40af" }}>
                      {ocrProgress.completedQuestions} of {ocrProgress.totalQuestions} questions transcribed
                    </span>
                    <span style={{ color: "#2563eb", fontFamily: "monospace", fontSize: "14px" }}>
                      {Math.round((ocrProgress.completedQuestions / Math.max(1, ocrProgress.totalQuestions)) * 100)}%
                    </span>
                  </div>
                  <div style={{
                    width: "100%",
                    height: "12px",
                    backgroundColor: "#e2e8f0",
                    borderRadius: "999px",
                    overflow: "hidden",
                    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.06)"
                  }}>
                    <div style={{
                      width: `${Math.round((ocrProgress.completedQuestions / Math.max(1, ocrProgress.totalQuestions)) * 100)}%`,
                      height: "100%",
                      background: "linear-gradient(90deg, #3b82f6, #2563eb, #1d4ed8)",
                      borderRadius: "999px",
                      transition: "width 0.4s ease",
                      boxShadow: "0 0 10px rgba(37, 99, 235, 0.3)"
                    }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "6px", fontSize: "11px", color: "var(--muted)" }}>
                    <span>
                      {ocrProgress.totalBatches > 1
                        ? `Batch ${ocrProgress.currentBatch} of ${ocrProgress.totalBatches} (Questions ${ocrProgress.batchStart}–${ocrProgress.batchEnd})`
                        : "Single batch processing"}
                    </span>
                    <span>Please keep this window open</span>
                  </div>
                </div>
              ) : (
                <div style={{ maxWidth: "420px", margin: "0 auto 18px auto", width: "100%" }}>
                  <div style={{
                    width: "100%",
                    height: "8px",
                    backgroundColor: "#e2e8f0",
                    borderRadius: "999px",
                    overflow: "hidden",
                    position: "relative"
                  }}>
                    <div style={{
                      position: "absolute",
                      inset: "0",
                      width: "40%",
                      background: "linear-gradient(90deg, #38bdf8, #2563eb, #38bdf8)",
                      borderRadius: "999px",
                      animation: "pulseGlow 1.5s infinite ease-in-out"
                    }} />
                  </div>
                </div>
              )}

              <h3 style={{ margin: "0 0 6px 0", color: "#0f172a", fontSize: "18px", fontWeight: "700" }}>
                {ocrStatusMessage || "Reading your math problems..."}
              </h3>
              <p className="question-meta" style={{ maxWidth: "460px", margin: "0 auto 20px auto", fontSize: "13px", color: "var(--muted)" }}>
                Checking equations, formulas, numbers, and diagrams so you can practice on your slate.
              </p>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setShowCancelConfirm(true)}
                style={{
                  fontSize: "13px",
                  padding: "7px 22px",
                  borderRadius: "8px",
                  color: "#dc2626",
                  borderColor: "#fca5a5",
                  backgroundColor: "#fff"
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              border: "1px solid var(--border)",
              padding: "20px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.04)"
            }}>
              {/* Tabs */}
              <div style={{
                display: "flex",
                gap: "8px",
                borderBottom: "1px solid var(--border)",
                paddingBottom: "14px",
                marginBottom: "20px",
                flexWrap: "wrap"
              }}>
                <button
                  type="button"
                  onClick={() => {
                    stopCamera();
                    setInputTab("upload");
                  }}
                  className={`tab-button ${inputTab === "upload" ? "active" : ""}`}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "none",
                    fontWeight: "600",
                    fontSize: "13px",
                    cursor: "pointer",
                    backgroundColor: inputTab === "upload" ? "#3b82f6" : "#f1f5f9",
                    color: inputTab === "upload" ? "#ffffff" : "#475569"
                  }}
                >
                  {creationMode === "multiple" ? "📁 Upload Worksheet" : "📁 Upload File"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setInputTab("camera");
                    if (!hasGetUserMedia) {
                      cameraInputRef.current?.click();
                    } else {
                      startCamera(currentFacingMode);
                    }
                  }}
                  className={`tab-button ${inputTab === "camera" ? "active" : ""}`}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "none",
                    fontWeight: "600",
                    fontSize: "13px",
                    cursor: "pointer",
                    backgroundColor: inputTab === "camera" ? "#3b82f6" : "#f1f5f9",
                    color: inputTab === "camera" ? "#ffffff" : "#475569"
                  }}
                >
                  📷 Take Photo
                </button>

                {creationMode === "single" && (
                  <button
                    type="button"
                    onClick={() => {
                      stopCamera();
                      setInputTab("stylus");
                    }}
                    className={`tab-button ${inputTab === "stylus" ? "active" : ""}`}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "8px",
                      border: "none",
                      fontWeight: "600",
                      fontSize: "13px",
                      cursor: "pointer",
                      backgroundColor: inputTab === "stylus" ? "#3b82f6" : "#f1f5f9",
                      color: inputTab === "stylus" ? "#ffffff" : "#475569"
                    }}
                  >
                    ✍️ Write with Stylus
                  </button>
                )}
              </div>

              {/* TAB 1: UPLOAD */}
              {inputTab === "upload" && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleFileSelect(e.target.files[0]);
                      }
                    }}
                  />

                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (e.dataTransfer.files?.[0]) {
                        handleFileSelect(e.dataTransfer.files[0]);
                      }
                    }}
                    style={{
                      border: "2px dashed #93c5fd",
                      borderRadius: "12px",
                      padding: "48px 24px",
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: "#f8fafc",
                      transition: "border-color 0.2s"
                    }}
                  >
                    <div style={{ fontSize: "40px", marginBottom: "12px" }}>📄</div>
                    <div style={{ fontWeight: "700", fontSize: "16px", color: "#1e293b", marginBottom: "4px" }}>
                      {creationMode === "multiple"
                        ? "Click or Drag & Drop Homework / Assignment Sheet"
                        : "Click or Drag & Drop Math Problem Image"}
                    </div>
                    <div className="question-meta" style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "16px" }}>
                      Supports PNG, JPG, WEBP, or PDF. You can also paste directly with Ctrl+V.
                    </div>
                    {selectedFileName && (
                      <div style={{ marginBottom: "14px", fontSize: "13px", color: "#2563eb", fontWeight: "600" }}>
                        📎 Selected: {selectedFileName}
                      </div>
                    )}
                    <button
                      type="button"
                      className="primary-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      style={{ padding: "8px 20px", fontSize: "13px", fontWeight: "600" }}
                    >
                      Browse Files
                    </button>
                  </div>
                </div>
              )}

              {/* TAB 2: CAMERA */}
              {inputTab === "camera" && (
                <div className="camera-viewfinder-wrapper">
                  {/* Hidden fallback file input for mobile devices if needed */}
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleFileSelect(e.target.files[0]);
                      }
                    }}
                  />

                  {!hasGetUserMedia ? (
                    /* In non-secure contexts (HTTP on LAN IP) or browsers without WebRTC: show native camera capture card */
                    <div className="camera-state-box" style={{ padding: "36px 20px" }}>
                      <div style={{ fontSize: "44px", marginBottom: "12px" }}>📸</div>
                      <div style={{ fontSize: "17px", fontWeight: "700", color: "#0f172a", marginBottom: "6px" }}>
                        Take Photo of Your {creationMode === "multiple" ? "Worksheet" : "Problem"}
                      </div>
                      <div style={{ fontSize: "13px", color: "var(--muted)", maxWidth: "380px", margin: "0 auto 20px auto", lineHeight: "1.5" }}>
                        {isMobileDevice
                          ? "Tap below to open your device camera and take a clear picture of your question."
                          : "Live webcam streaming requires a secure connection (HTTPS). You can browse or drag & drop files instead."}
                      </div>
                      <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
                        {isMobileDevice && (
                          <button
                            type="button"
                            className="camera-shutter-btn"
                            onClick={() => cameraInputRef.current?.click()}
                            style={{ padding: "12px 28px", fontSize: "15px" }}
                          >
                            <span style={{ fontSize: "18px" }}>📸</span> Open Camera to Take Photo
                          </button>
                        )}
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => {
                            stopCamera();
                            setInputTab("upload");
                          }}
                          style={{ padding: "12px 20px", fontSize: "14px", fontWeight: "600" }}
                        >
                          📁 Upload File Instead
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Secure context (HTTPS or localhost) with WebRTC support */
                    <>
                      {/* Viewfinder Screen - Video element is permanently in DOM */}
                      <div className="camera-viewfinder-screen">
                        {/* Video stream element */}
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="camera-viewfinder-video"
                          style={{
                            display: (cameraActive && !isCameraLoading) ? "block" : "none"
                          }}
                        />

                        {/* Camera Loading State */}
                        {isCameraLoading && (
                          <div style={{ textAlign: "center", color: "#f8fafc", padding: "20px" }}>
                            <div
                              className="spinner"
                              style={{
                                width: "38px",
                                height: "38px",
                                borderWidth: "3px",
                                borderColor: "#38bdf8",
                                borderTopColor: "transparent",
                                margin: "0 auto 12px auto"
                              }}
                            />
                            <div style={{ fontSize: "15px", fontWeight: "700" }}>Starting camera...</div>
                            <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>
                              Please allow camera access in your browser
                            </div>
                          </div>
                        )}

                        {/* Camera Error / Permission Blocked State */}
                        {cameraError && !isCameraLoading && (
                          <div style={{ textAlign: "center", color: "#f8fafc", padding: "24px 20px", maxWidth: "420px" }}>
                            <div style={{ fontSize: "36px", marginBottom: "8px" }}>📷</div>
                            <div style={{ fontSize: "16px", fontWeight: "700", marginBottom: "6px", color: "#f87171" }}>
                              Camera Unavailable
                            </div>
                            <div style={{ fontSize: "13px", color: "#cbd5e1", lineHeight: "1.5", marginBottom: "18px" }}>
                              {cameraError}
                            </div>
                            <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
                              <button
                                type="button"
                                className="primary-button"
                                onClick={() => startCamera(currentFacingMode)}
                                style={{ padding: "8px 20px", fontSize: "13px" }}
                              >
                                🔄 Retry Camera
                              </button>
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => {
                                  stopCamera();
                                  setInputTab("upload");
                                }}
                                style={{ padding: "8px 18px", fontSize: "13px", backgroundColor: "#ffffff", color: "#0f172a" }}
                              >
                                📁 Upload File Instead
                              </button>
                            </div>
                            {isMobileDevice && (
                              <div style={{ marginTop: "14px" }}>
                                <button
                                  type="button"
                                  onClick={() => cameraInputRef.current?.click()}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "#93c5fd",
                                    textDecoration: "underline",
                                    fontSize: "12px",
                                    cursor: "pointer"
                                  }}
                                >
                                  Or snap photo with device camera
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Scanning guides / corner brackets overlay on top of active video */}
                        {cameraActive && !isCameraLoading && (
                          <div className="camera-viewfinder-overlay">
                            {/* Top row with live indicator */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div className="camera-status-pill">
                                <span className="camera-status-dot" />
                                Live Camera
                              </div>
                            </div>

                            {/* Viewfinder Target Framing Box */}
                            <div className="camera-framing-box">
                              <div className="camera-corner-tl" />
                              <div className="camera-corner-tr" />
                              <div className="camera-corner-bl" />
                              <div className="camera-corner-br" />
                              <div className="camera-framing-hint">
                                Align {creationMode === "multiple" ? "worksheet" : "problem"} inside frame
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Controls Bar Below Video */}
                      {cameraActive && !isCameraLoading && (
                        <div className="camera-controls-bar">
                          {/* Main Shutter Button */}
                          <button
                            type="button"
                            className="camera-shutter-btn"
                            onClick={capturePhoto}
                          >
                            <span style={{ fontSize: "18px" }}>📸</span> Take Photo
                          </button>

                          {/* Flip Camera Button (front / back) - only if multiple cameras */}
                          {videoDevices.length > 1 && (
                            <button
                              type="button"
                              className="secondary-button"
                              onClick={flipCamera}
                              title="Switch camera"
                              style={{
                                padding: "11px 18px",
                                fontSize: "13px",
                                fontWeight: "600",
                                borderRadius: "10px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px"
                              }}
                            >
                              <span>🔄</span> Flip Camera
                            </button>
                          )}

                          {/* On mobile, option to switch to native camera app */}
                          {isMobileDevice && (
                            <button
                              type="button"
                              className="secondary-button"
                              onClick={() => cameraInputRef.current?.click()}
                              title="Snap photo with native camera app"
                              style={{
                                padding: "11px 18px",
                                fontSize: "13px",
                                fontWeight: "600",
                                borderRadius: "10px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px"
                              }}
                            >
                              <span>📱</span> Device Camera
                            </button>
                          )}

                          {/* Stop / Cancel Camera Button */}
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() => {
                              stopCamera();
                              setInputTab("upload");
                            }}
                            style={{
                              padding: "11px 18px",
                              fontSize: "13px",
                              borderRadius: "10px"
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* TAB 3: STYLUS CANVAS (Single Question Only) */}
              {inputTab === "stylus" && creationMode === "single" && (
                <div>
                  {/* Canvas Toolbar */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    marginBottom: "12px",
                    flexWrap: "wrap",
                    gap: "10px"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button
                        type="button"
                        onClick={() => {
                          setCanvasTool("pen");
                          setPenColor("#0f172a");
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          border: `1.5px solid ${canvasTool === "pen" && penColor === "#0f172a" ? "#0f172a" : "#cbd5e1"}`,
                          backgroundColor: canvasTool === "pen" && penColor === "#0f172a" ? "#0f172a" : "#ffffff",
                          color: canvasTool === "pen" && penColor === "#0f172a" ? "#ffffff" : "#0f172a",
                          fontSize: "12px",
                          fontWeight: "700",
                          cursor: "pointer"
                        }}
                      >
                        <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#0f172a", display: "inline-block" }} />
                        Black Pen
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setCanvasTool("pen");
                          setPenColor("#1d4ed8");
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          border: `1.5px solid ${canvasTool === "pen" && penColor === "#1d4ed8" ? "#1d4ed8" : "#cbd5e1"}`,
                          backgroundColor: canvasTool === "pen" && penColor === "#1d4ed8" ? "#1d4ed8" : "#ffffff",
                          color: canvasTool === "pen" && penColor === "#1d4ed8" ? "#ffffff" : "#1d4ed8",
                          fontSize: "12px",
                          fontWeight: "700",
                          cursor: "pointer"
                        }}
                      >
                        <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#1d4ed8", display: "inline-block" }} />
                        Blue Pen
                      </button>

                      <button
                        type="button"
                        onClick={() => setCanvasTool("eraser")}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "6px",
                          border: `1.5px solid ${canvasTool === "eraser" ? "#ef4444" : "#cbd5e1"}`,
                          backgroundColor: canvasTool === "eraser" ? "#fee2e2" : "#ffffff",
                          color: canvasTool === "eraser" ? "#b91c1c" : "#475569",
                          fontSize: "12px",
                          fontWeight: "700",
                          cursor: "pointer"
                        }}
                      >
                        🧹 Eraser
                      </button>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => setCanvasLines([])}
                        disabled={!canvasLines.length}
                        style={{ padding: "6px 12px", fontSize: "12px" }}
                      >
                        Clear Slate
                      </button>
                      <button
                        type="button"
                        className="primary-button"
                        onClick={convertStylusCanvas}
                        disabled={!canvasLines.length}
                        style={{ padding: "6px 16px", fontSize: "13px", fontWeight: "700" }}
                      >
                        Transcribe Ink
                      </button>
                    </div>
                  </div>

                  {/* Konva Stylus Surface */}
                  <div
                    ref={canvasContainerRef}
                    style={{
                      border: "1.5px solid #94a3b8",
                      borderRadius: "10px",
                      overflow: "hidden",
                      backgroundColor: "#ffffff",
                      backgroundImage: "radial-gradient(#e2e8f0 1.2px, transparent 1.2px)",
                      backgroundSize: "20px 20px",
                      touchAction: "none"
                    }}
                  >
                    <Stage
                      ref={stageRef}
                      width={canvasSize.width}
                      height={canvasSize.height}
                      onPointerDown={handlePointerDown}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerUp}
                    >
                      <Layer>
                        {canvasLines.map((line, i) => (
                          <Line
                            key={i}
                            points={line.points}
                            stroke={line.color}
                            strokeWidth={line.strokeWidth || 3}
                            tension={0.4}
                            lineCap="round"
                            lineJoin="round"
                            globalCompositeOperation={line.isEraser ? "destination-out" : "source-over"}
                          />
                        ))}
                      </Layer>
                    </Stage>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── STAGE 2: REVIEW & CUSTOMIZE ── */}
      {creationStep === "review" && (
        <div>
          {/* Top Review Toolbar */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "18px",
            backgroundColor: "#ffffff",
            padding: "12px 18px",
            borderRadius: "10px",
            border: "1px solid var(--border)",
            flexWrap: "wrap",
            gap: "12px"
          }}>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setCreationStep("input")}
              style={{ fontSize: "13px", padding: "6px 14px" }}
            >
              ← Re-capture / Upload Different File
            </button>

            {creationMode === "single" ? (
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => handleSaveSingleQuestion(false)}
                  disabled={isSaving}
                  style={{ fontSize: "13px", padding: "8px 18px", fontWeight: "600" }}
                >
                  💾 Save to My Questions
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => handleSaveSingleQuestion(true)}
                  disabled={isSaving}
                  style={{ fontSize: "13px", padding: "8px 20px", fontWeight: "700" }}
                >
                  🚀 Save & Solve on Slate
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => handleSaveQuestionSet(false)}
                  disabled={isSaving}
                  style={{ fontSize: "13px", padding: "8px 18px", fontWeight: "600" }}
                >
                  💾 Save All Questions
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => handleSaveQuestionSet(true)}
                  disabled={isSaving}
                  style={{ fontSize: "13px", padding: "8px 20px", fontWeight: "700" }}
                >
                  🚀 Save All & Start Solving (Q1)
                </button>
              </div>
            )}
          </div>

          {/* SINGLE QUESTION REVIEW LAYOUT */}
          {creationMode === "single" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: "20px" }}>
              {/* Left Column: Math Preview */}
              <div style={{
                backgroundColor: "#ffffff",
                borderRadius: "14px",
                border: "1.5px solid #cbd5e1",
                padding: "20px",
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)"
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                  <div style={{ fontWeight: "700", fontSize: "15px", color: "#0f172a" }}>Math Preview</div>
                  <span className={`difficulty-badge level-${singleFormData.difficulty.toLowerCase()}`}>
                    {singleFormData.difficulty}
                  </span>
                </div>

                <div style={{
                  padding: "20px 24px",
                  borderRadius: "10px",
                  backgroundColor: "#f8fafc",
                  border: "1.5px solid #e2e8f0",
                  minHeight: "180px",
                  maxHeight: "420px",
                  overflowY: "auto"
                }}>
                  {singleFormData.latex ? (
                    <BlockMath
                      math={formatLatexForDisplay(singleFormData.latex)}
                      renderError={() => (
                        <div style={{ color: "#334155", fontSize: "13px", padding: "10px 14px", backgroundColor: "#f1f5f9", borderRadius: "8px", fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                          {singleFormData.latex}
                        </div>
                      )}
                    />
                  ) : (
                    <div className="question-meta" style={{ textAlign: "center", padding: "30px" }}>
                      No math equation written yet.
                    </div>
                  )}
                </div>

                {singleFormData.imageUrl && (
                  <div style={{ marginTop: "16px" }}>
                    <div style={{ fontSize: "12px", fontWeight: "600", color: "var(--muted)", marginBottom: "6px" }}>
                      Original Captured Reference:
                    </div>
                    <img
                      src={singleFormData.imageUrl}
                      alt="Question source"
                      style={{
                        maxHeight: "180px",
                        maxWidth: "100%",
                        borderRadius: "8px",
                        border: "1.5px solid #cbd5e1",
                        objectFit: "contain"
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Right Column: Editable Fields & Curriculum Tags */}
              <div style={{
                backgroundColor: "#ffffff",
                borderRadius: "14px",
                border: "1.5px solid #cbd5e1",
                padding: "20px",
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)"
              }}>
                <div style={{ fontWeight: "700", fontSize: "15px", color: "#0f172a", marginBottom: "14px" }}>
                  Problem Details & Topics
                </div>

                {/* Title */}
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "4px", color: "#475569" }}>
                    Problem Title
                  </label>
                  <input
                    type="text"
                    value={singleFormData.title}
                    onChange={(e) => setSingleFormData({ ...singleFormData, title: e.target.value })}
                    placeholder="e.g. Quadratic Roots with Discriminant"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: "8px",
                      border: "1.5px solid #cbd5e1",
                      backgroundColor: "#f8fafc",
                      color: "#0f172a",
                      fontWeight: "600",
                      fontSize: "14px",
                      boxSizing: "border-box"
                    }}
                  />
                </div>

                {/* Math Equation Textarea */}
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "4px", color: "#475569" }}>
                    Math Equation
                  </label>
                  <textarea
                    rows={4}
                    value={singleFormData.latex}
                    onChange={(e) => setSingleFormData({ ...singleFormData, latex: e.target.value })}
                    onBlur={(e) => setSingleFormData((prev) => ({ ...prev, latex: repairLatexEscapes(e.target.value) }))}
                    placeholder="e.g. x^2 - 4x + 4 = 0"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "1.5px solid #cbd5e1",
                      fontFamily: "monospace",
                      fontSize: "13px",
                      backgroundColor: "#ffffff",
                      color: "#0f172a",
                      boxSizing: "border-box",
                      lineHeight: "1.5"
                    }}
                  />
                </div>

                {/* Grade & Difficulty */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "4px", color: "#475569" }}>
                      Grade Level
                    </label>
                    <select
                      value={singleFormData.grade}
                      onChange={(e) => setSingleFormData({ ...singleFormData, grade: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1.5px solid #cbd5e1",
                        backgroundColor: "#f8fafc",
                        color: "#0f172a",
                        fontWeight: "600",
                        fontSize: "13px",
                        boxSizing: "border-box"
                      }}
                    >
                      {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "4px", color: "#475569" }}>
                      Difficulty Level
                    </label>
                    <select
                      value={singleFormData.difficulty}
                      onChange={(e) => setSingleFormData({ ...singleFormData, difficulty: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1.5px solid #cbd5e1",
                        backgroundColor: "#f8fafc",
                        color: "#0f172a",
                        fontWeight: "600",
                        fontSize: "13px",
                        boxSizing: "border-box"
                      }}
                    >
                      <option value="LOT">LOT (Foundational)</option>
                      <option value="MOT">MOT (Standard)</option>
                      <option value="HOT">HOT (Challenging)</option>
                    </select>
                  </div>
                </div>

                {/* Curriculum Chapters Checkboxes */}
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "6px", color: "#475569" }}>
                    Curriculum Chapters ({CURRICULUM[singleFormData.grade]?.length || 0} in {singleFormData.grade})
                  </label>
                  <div style={{
                    maxHeight: "140px",
                    overflowY: "auto",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: "1.5px solid #cbd5e1",
                    backgroundColor: "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px"
                  }}>
                    {(CURRICULUM[singleFormData.grade] || ALL_CHAPTERS).map((ch) => {
                      const checked = singleFormData.chapters.includes(ch);
                      return (
                        <label
                          key={ch}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "4px 6px",
                            fontSize: "12px",
                            cursor: "pointer",
                            borderRadius: "4px",
                            backgroundColor: checked ? "#eff6ff" : "transparent"
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSingleChapter(ch)}
                            style={{ width: "16px", height: "16px", margin: 0, flexShrink: 0, cursor: "pointer" }}
                          />
                          <span style={{ fontWeight: checked ? "600" : "normal", color: checked ? "#1d4ed8" : "#334155" }}>{ch}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Single Question Tutor Assignment Dispatch Section */}
              {isTutor && (
                <div style={{
                  gridColumn: "1 / -1",
                  backgroundColor: "#ffffff",
                  borderRadius: "14px",
                  border: "1.5px solid #cbd5e1",
                  padding: "20px",
                  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
                  marginTop: "4px"
                }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
                    <input
                      type="checkbox"
                      checked={singleAssignment.assignToClassroom}
                      onChange={(e) => setSingleAssignment({ ...singleAssignment, assignToClassroom: e.target.checked })}
                      style={{ width: "18px", height: "18px", accentColor: "#4f46e5" }}
                    />
                    <span>📋 Assign this question directly to my students as homework</span>
                  </label>

                  {singleAssignment.assignToClassroom && (
                    <div style={{ marginTop: "16px", borderTop: "1px dashed #cbd5e1", paddingTop: "16px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "18px", marginBottom: "16px" }}>
                        <div>
                          <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
                            Assign to Students
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", marginBottom: "8px", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={singleAssignment.assignedAll}
                              onChange={(e) => setSingleAssignment({ ...singleAssignment, assignedAll: e.target.checked })}
                            />
                            <strong>All students in classroom ({rosterStudents.length})</strong>
                          </label>

                          {!singleAssignment.assignedAll && (
                            <div style={{ maxHeight: "140px", overflowY: "auto", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "8px 12px", backgroundColor: "#ffffff" }}>
                              {rosterStudents.map((st) => {
                                const checked = singleAssignment.assignedTo.includes(st.studentUid);
                                return (
                                  <label key={st.studentUid} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", padding: "4px 0", cursor: "pointer" }}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSingleAssignment({ ...singleAssignment, assignedTo: [...singleAssignment.assignedTo, st.studentUid] });
                                        } else {
                                          setSingleAssignment({ ...singleAssignment, assignedTo: singleAssignment.assignedTo.filter((id) => id !== st.studentUid) });
                                        }
                                      }}
                                    />
                                    <span>{st.name || st.username} {st.grade ? `(${st.grade})` : ""}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div>
                          <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
                            Due Date (Optional)
                          </label>
                          <input
                            type="date"
                            value={singleAssignment.dueDate}
                            onChange={(e) => setSingleAssignment({ ...singleAssignment, dueDate: e.target.value })}
                            style={{
                              width: "100%",
                              padding: "9px 14px",
                              borderRadius: "8px",
                              border: "1.5px solid #cbd5e1",
                              fontSize: "14px",
                              backgroundColor: "#f8fafc",
                              boxSizing: "border-box"
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
                          Teacher Instructions / Note (Optional)
                        </label>
                        <input
                          type="text"
                          value={singleAssignment.instructions}
                          onChange={(e) => setSingleAssignment({ ...singleAssignment, instructions: e.target.value })}
                          placeholder="e.g. Try solving on your Digital Slate or take clear photos of your notebook."
                          style={{
                            width: "100%",
                            padding: "9px 14px",
                            borderRadius: "8px",
                            border: "1.5px solid #cbd5e1",
                            fontSize: "13px",
                            backgroundColor: "#f8fafc",
                            boxSizing: "border-box"
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* MULTIPLE QUESTIONS REVIEW LAYOUT */}
          {creationMode === "multiple" && (
            <div>
              {/* Assignment Meta Banner (Clear Outline & Subtle Fill) */}
              <div className="review-meta-banner">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "18px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
                      Assignment / Question Set Label <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={setFormData.title}
                      onChange={(e) => setSetFormData({ ...setFormData, title: e.target.value })}
                      placeholder="e.g. Circle - Homework 3"
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "8px",
                        border: "1.5px solid #cbd5e1",
                        fontWeight: "600",
                        fontSize: "14px",
                        backgroundColor: "#f8fafc",
                        color: "#0f172a",
                        boxSizing: "border-box",
                        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.03)"
                      }}
                    />
                    <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "4px" }}>
                      This name appears at the top of your slate while practicing.
                    </div>
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
                      Grade Level
                    </label>
                    <select
                      value={setFormData.grade}
                      onChange={(e) => handleSetGradeChange(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "8px",
                        border: "1.5px solid #cbd5e1",
                        fontWeight: "600",
                        fontSize: "14px",
                        backgroundColor: "#f8fafc",
                        color: "#0f172a",
                        boxSizing: "border-box",
                        cursor: "pointer"
                      }}
                    >
                      {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "4px" }}>
                      Curriculum topics will match this grade level.
                    </div>
                  </div>
                </div>

                {/* Tutor Assignment Dispatch Section */}
                {isTutor && (
                  <div style={{ marginTop: "18px", borderTop: "1px dashed #cbd5e1", paddingTop: "18px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "18px", marginBottom: "16px" }}>
                      <div>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
                          Assign to Students
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", marginBottom: "8px", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(setFormData.assignedAll ?? true)}
                            onChange={(e) => setSetFormData((prev) => ({ ...prev, assignedAll: e.target.checked }))}
                          />
                          <strong>All students in classroom ({rosterStudents.length})</strong>
                        </label>

                        {!(setFormData.assignedAll ?? true) && (
                          <div style={{ maxHeight: "140px", overflowY: "auto", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "8px 12px", backgroundColor: "#ffffff" }}>
                            {rosterStudents.map((st) => {
                              const assignedList = Array.isArray(setFormData.assignedTo) ? setFormData.assignedTo : [];
                              const checked = assignedList.includes(st.studentUid);
                              return (
                                <label key={st.studentUid} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", padding: "4px 0", cursor: "pointer" }}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSetFormData((prev) => ({
                                          ...prev,
                                          assignedTo: [...(Array.isArray(prev.assignedTo) ? prev.assignedTo : []), st.studentUid]
                                        }));
                                      } else {
                                        setSetFormData((prev) => ({
                                          ...prev,
                                          assignedTo: (Array.isArray(prev.assignedTo) ? prev.assignedTo : []).filter((id) => id !== st.studentUid)
                                        }));
                                      }
                                    }}
                                  />
                                  <span>{st.name} ({st.grade})</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
                          Due Date (Optional)
                        </label>
                        <input
                          type="date"
                          value={setFormData.dueDate}
                          onChange={(e) => setSetFormData({ ...setFormData, dueDate: e.target.value })}
                          style={{
                            width: "100%",
                            padding: "9px 14px",
                            borderRadius: "8px",
                            border: "1.5px solid #cbd5e1",
                            fontSize: "14px",
                            backgroundColor: "#f8fafc",
                            boxSizing: "border-box"
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "6px", color: "#0f172a" }}>
                        Teacher Instructions / Note (Optional)
                      </label>
                      <input
                        type="text"
                        value={setFormData.instructions}
                        onChange={(e) => setSetFormData({ ...setFormData, instructions: e.target.value })}
                        placeholder="e.g. Try solving on your Digital Slate or take clear photos of your notebook."
                        style={{
                          width: "100%",
                          padding: "9px 14px",
                          borderRadius: "8px",
                          border: "1.5px solid #cbd5e1",
                          fontSize: "13px",
                          backgroundColor: "#f8fafc",
                          boxSizing: "border-box"
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Questions Counter & Add Button */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
                flexWrap: "wrap",
                gap: "10px"
              }}>
                <div style={{ fontWeight: "700", fontSize: "16px", color: "#0f172a" }}>
                  Questions in this Assignment ({setFormData.questions.length})
                </div>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={addBlankQuestion}
                  style={{ fontSize: "13px", padding: "6px 14px", fontWeight: "600" }}
                >
                  + Add Another Question
                </button>
              </div>

              {/* Questions List Cards */}
              <ErrorBoundary fallback={
                <div style={{ padding: "24px", backgroundColor: "#fff", borderRadius: "12px", border: "1.5px solid #cbd5e1", textAlign: "center" }}>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b", marginBottom: "6px" }}>Questions Loaded ({setFormData.questions.length})</div>
                  <div style={{ fontSize: "13px", color: "#64748b" }}>Some formulas encountered an issue during preview, but all questions are saved and ready.</div>
                </div>
              }>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {setFormData.questions.map((q, idx) => {
                  const isExpanded = expandedQuestionIndexes.has(idx);
                  const qGrade = q.grade || setFormData.grade || "Grade 11";
                  const qChapters = Array.isArray(q.chapters) && q.chapters.length
                    ? q.chapters
                    : (q.chapter ? [q.chapter] : ["General"]);
                  const availableChapters = CURRICULUM[qGrade] || ALL_CHAPTERS;

                  return (
                    <div
                      key={idx}
                      style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "14px",
                        border: "1.5px solid #cbd5e1",
                        overflow: "hidden",
                        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
                        transition: "all 0.15s ease"
                      }}
                    >
                      {/* Question Card Header */}
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 18px",
                        backgroundColor: "#f8fafc",
                        borderBottom: "1.5px solid #e2e8f0",
                        flexWrap: "wrap",
                        gap: "10px"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: "240px", flexWrap: "wrap" }}>
                          <span style={{
                            backgroundColor: "#eff6ff",
                            color: "#1d4ed8",
                            border: "1.5px solid #bfdbfe",
                            padding: "3px 9px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "700"
                          }}>
                            Q{idx + 1}
                          </span>
                          <input
                            type="text"
                            value={q.title || `Problem ${idx + 1}`}
                            onChange={(e) => updateQuestionItem(idx, "title", e.target.value)}
                            placeholder="Question Title"
                            style={{
                              border: "1.5px solid #cbd5e1",
                              borderRadius: "8px",
                              backgroundColor: "#ffffff",
                              fontWeight: "700",
                              fontSize: "14px",
                              color: "#0f172a",
                              padding: "6px 12px",
                              flex: 1,
                              maxWidth: "300px",
                              boxSizing: "border-box",
                              boxShadow: "inset 0 1px 2px rgba(0,0,0,0.03)"
                            }}
                          />
                          {/* Chapter Tags in Header */}
                          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            {qChapters.slice(0, 2).map((ch, ci) => (
                              <span key={ci} className="frame-tag-pill focus-pill" style={{ fontSize: "11px", padding: "3px 8px", border: "1px solid #bfdbfe" }}>
                                {ch}
                              </span>
                            ))}
                            {qChapters.length > 2 && (
                              <span style={{ fontSize: "11px", color: "var(--muted)", alignSelf: "center" }}>
                                +{qChapters.length - 2}
                              </span>
                            )}
                          </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {/* Difficulty Selector */}
                          <select
                            value={q.difficulty || "MOT"}
                            onChange={(e) => updateQuestionItem(idx, "difficulty", e.target.value)}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              border: "1.5px solid #cbd5e1",
                              fontSize: "12px",
                              fontWeight: "600",
                              backgroundColor: "#ffffff",
                              color: "#334155",
                              cursor: "pointer"
                            }}
                          >
                            <option value="LOT">LOT</option>
                            <option value="MOT">MOT</option>
                            <option value="HOT">HOT</option>
                          </select>

                          {/* Reorder Buttons */}
                          <button
                            type="button"
                            onClick={() => moveQuestionUp(idx)}
                            disabled={idx === 0}
                            title="Move Up"
                            style={{
                              padding: "6px 10px",
                              borderRadius: "8px",
                              border: "1.5px solid #cbd5e1",
                              backgroundColor: "#ffffff",
                              cursor: idx === 0 ? "not-allowed" : "pointer",
                              fontSize: "11px"
                            }}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            onClick={() => moveQuestionDown(idx)}
                            disabled={idx === setFormData.questions.length - 1}
                            title="Move Down"
                            style={{
                              padding: "6px 10px",
                              borderRadius: "8px",
                              border: "1.5px solid #cbd5e1",
                              backgroundColor: "#ffffff",
                              cursor: idx === setFormData.questions.length - 1 ? "not-allowed" : "pointer",
                              fontSize: "11px"
                            }}
                          >
                            ▼
                          </button>

                          {/* Delete Question */}
                          <button
                            type="button"
                            onClick={() => deleteQuestionItem(idx)}
                            title="Delete this question"
                            style={{
                              padding: "6px 10px",
                              borderRadius: "8px",
                              border: "1.5px solid #fecaca",
                              backgroundColor: "#fef2f2",
                              color: "#dc2626",
                              cursor: "pointer",
                              fontSize: "12px"
                            }}
                          >
                            🗑️
                          </button>

                          {/* Expand/Collapse Toggle */}
                          <button
                            type="button"
                            onClick={() => toggleQuestionExpanded(idx)}
                            style={{
                              padding: "6px 14px",
                              borderRadius: "8px",
                              border: "1.5px solid #cbd5e1",
                              backgroundColor: isExpanded ? "#e2e8f0" : "#ffffff",
                              fontWeight: "600",
                              fontSize: "12px",
                              color: "#1e293b",
                              cursor: "pointer"
                            }}
                          >
                            {isExpanded ? "Done Editing ▲" : "Edit Topics & Equation ▼"}
                          </button>
                        </div>
                      </div>

                      {/* Question KaTeX Preview */}
                      <div style={{ padding: "16px 20px", backgroundColor: "#ffffff" }}>
                        <div style={{
                          padding: "18px 22px",
                          borderRadius: "10px",
                          backgroundColor: "#f8fafc",
                          border: "1.5px solid #e2e8f0",
                          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.02)"
                        }}>
                          {q.latex ? (
                            <BlockMath
                              math={formatLatexForDisplay(q.latex)}
                              renderError={() => (
                                <div style={{ color: "#334155", fontSize: "13px", padding: "10px 14px", backgroundColor: "#f1f5f9", borderRadius: "8px", fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                                  {q.latex}
                                </div>
                              )}
                            />
                          ) : (
                            <span className="question-meta" style={{ fontStyle: "italic" }}>No math equation provided.</span>
                          )}
                        </div>
                      </div>

                      {/* Expandable Settings & Full-Width Equation Drawer */}
                      {isExpanded && (
                        <div style={{
                          padding: "18px 22px",
                          backgroundColor: "#f8fafc",
                          borderTop: "1.5px solid #e2e8f0",
                          display: "flex",
                          flexDirection: "column",
                          gap: "14px"
                        }}>
                          {/* Compact Top Bar: Chapter Selector Dropdown + Badges */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                              <span style={{ fontSize: "12px", fontWeight: "700", color: "#475569" }}>
                                Curriculum Topics:
                              </span>
                              <div className="chapter-dropdown-wrapper">
                                <button
                                  type="button"
                                  className="chapter-dropdown-btn"
                                  onClick={() => setOpenChapterDropdownIdx(openChapterDropdownIdx === idx ? null : idx)}
                                >
                                  <span>Select Topics ({qChapters.length})</span>
                                  <span style={{ fontSize: "10px", color: "#64748b" }}>▼</span>
                                </button>

                                {openChapterDropdownIdx === idx && (
                                  <div className="chapter-dropdown-popover">
                                    <div className="chapter-dropdown-popover-header">
                                      {qGrade} Topics
                                    </div>
                                    {availableChapters.map((ch) => {
                                      const isChecked = qChapters.includes(ch);
                                      return (
                                        <label
                                          key={ch}
                                          className={`chapter-dropdown-item ${isChecked ? "is-checked" : ""}`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleQuestionChapter(idx, ch)}
                                          />
                                          <span>{ch}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>

                              {/* Selected Chapter Badges */}
                              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                {qChapters.map((ch) => (
                                  <span
                                    key={ch}
                                    className="frame-tag-pill focus-pill"
                                    style={{ fontSize: "11px", padding: "3px 8px", border: "1px solid #bfdbfe" }}
                                  >
                                    {ch}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Full-width Math Equation Textarea */}
                          <div>
                            <label style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "6px", color: "#475569" }}>
                              Math Equation (LaTeX)
                            </label>
                            <textarea
                              rows={5}
                              value={q.latex || ""}
                              onChange={(e) => updateQuestionItem(idx, "latex", e.target.value)}
                              onBlur={(e) => updateQuestionItem(idx, "latex", repairLatexEscapes(e.target.value))}
                              placeholder="e.g. x^2 - 4x + 4 = 0"
                              style={{
                                width: "100%",
                                padding: "12px 14px",
                                borderRadius: "8px",
                                border: "1.5px solid #cbd5e1",
                                fontFamily: "monospace",
                                fontSize: "13px",
                                backgroundColor: "#ffffff",
                                color: "#0f172a",
                                boxSizing: "border-box",
                                lineHeight: "1.5",
                                boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.03)"
                              }}
                            />
                            <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "4px" }}>
                              You can review and edit equations here. The preview updates above automatically.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </ErrorBoundary>
            </div>
          )}
        </div>
      )}

      {/* ── EXIT CONFIRMATION MODAL ── */}
      {showExitConfirm && (
        <div style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(15, 23, 42, 0.6)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px"
        }}>
          <div style={{
            maxWidth: "460px",
            width: "100%",
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)"
          }}>
            <div style={{ fontSize: "28px", marginBottom: "10px" }}>⚠️</div>
            <h3 style={{ margin: "0 0 8px 0", color: "#0f172a", fontSize: "18px" }}>Leave without saving?</h3>
            <p className="question-meta" style={{ margin: "0 0 20px 0", fontSize: "13px", lineHeight: "1.5" }}>
              You have an unsaved question or worksheet. Leaving this page will discard your changes.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setShowExitConfirm(false)}
                style={{ padding: "8px 16px", fontSize: "13px" }}
              >
                Stay on Page
              </button>
              <button
                type="button"
                onClick={confirmExit}
                style={{
                  padding: "8px 18px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#ef4444",
                  color: "#ffffff",
                  fontWeight: "700",
                  fontSize: "13px",
                  cursor: "pointer"
                }}
              >
                Discard & Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px"
          }}
        >
          <div
            style={{
              maxWidth: "460px",
              width: "100%",
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)"
            }}
          >
            <div style={{ fontSize: "28px", marginBottom: "10px" }}>⚠️</div>
            <h3 style={{ margin: "0 0 8px 0", color: "#0f172a", fontSize: "18px", fontWeight: "700" }}>
              Cancel Worksheet Processing?
            </h3>
            <p className="question-meta" style={{ margin: "0 0 20px 0", fontSize: "13px", lineHeight: "1.5", color: "#475569" }}>
              {ocrProgress.phase === "transcribing" && ocrProgress.totalQuestions > 0
                ? `Worksheet transcription is currently at ${Math.round((ocrProgress.completedQuestions / Math.max(1, ocrProgress.totalQuestions)) * 100)}% (${ocrProgress.completedQuestions} of ${ocrProgress.totalQuestions} questions completed). Are you sure you want to cancel? Current progress will be discarded.`
                : "Worksheet scanning is currently in progress. Are you sure you want to stop and cancel?"}
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setShowCancelConfirm(false)}
                style={{ padding: "8px 16px", fontSize: "13px" }}
              >
                Keep Processing
              </button>
              <button
                type="button"
                onClick={() => {
                  cancelOcrTranscription();
                  setOcrError("Worksheet processing was cancelled by user.");
                }}
                style={{
                  padding: "8px 18px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#ef4444",
                  color: "#ffffff",
                  fontWeight: "700",
                  fontSize: "13px",
                  cursor: "pointer"
                }}
              >
                Yes, Stop & Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

